import { formatDateTime, formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useParams } from "react-router-dom";
import type { Service } from "@oathera/contract";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { StatusBadge } from "../../components/index.ts";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { serviceLabel, statusLevel, statusText } from "../overview/vocab.ts";
import { useService, isServiceParam, type ServiceDetail } from "./use-service.ts";
import { GoldenSignalsPanel } from "./GoldenSignalsPanel.tsx";
import { RouteTable } from "./RouteTable.tsx";
import { InstancesTable } from "./InstancesTable.tsx";
import { AdminDeepLink } from "./AdminDeepLink.tsx";
import { ServiceAdminConsole } from "./admin/ServiceAdminConsole.tsx";
import type { ServiceAdminSlug } from "./admin/use-service-admin.ts";
import "./service.css";

/**
 * The Service page (task 11.5; design 12.2 Service row). It is the drill-down target of the
 * Overview's service map (`/services/:service`). It composes the regions the design calls for over
 * two sources: the REST `GET /api/v1/services/{service}` snapshot (status, instance counts,
 * versions, profiles, golden signals, routes, instances with restarts, and the admin-link
 * registration) and the live stream VIEW the app-root driver already feeds — the golden-signals
 * panel draws the service's live metric series on top of the snapshot. Pausing the time-range
 * freezes both: the query key stops advancing and the stream buffers (design 12.2, FR-13).
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - invalid-service: the `:service` route param is validated against the contract `Service` enum;
 *   an unknown value is an honest "no such service" empty state, NOT a crash and NOT a request.
 * - restricted: the detail is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / empty / ready as the query resolves. A 404 (core does not know the service)
 *   reads as a distinct empty cause; a cold-core waking error defers to the shell's banner.
 */
export function ServiceScreen(): ReactNode {
  const { service: param } = useParams();
  const valid = isServiceParam(param);
  const { role } = useIdentity();
  const { live } = useTimeRange();

  // The hook obeys the rules of hooks regardless of validity; it is disabled for an invalid param so
  // no request goes out for a service that cannot exist. A stable placeholder service keeps the
  // query key well-typed while disabled.
  const service: Service = valid ? param : "idp";

  // The three HOSTED services (IDP / AIS / VCI) render their migrated ADMIN CONSOLE here instead of
  // the telemetry detail — this screen is the drill-down target of the Overview KYA map, and the
  // operator manages the service from here (replacing each service's standalone admin UI). Other
  // services keep the telemetry detail below.
  const adminSlug: ServiceAdminSlug | null =
    valid && (param === "idp" || param === "ais" || param === "vci") ? param : null;

  const { query, window } = useService(service, { enabled: valid && adminSlug === null });

  const title = valid ? `Service: ${serviceLabel(service)}` : "Service";

  // Admin console path (hosted services). Still role-gated: the console reads the service admin API
  // through the session-gated BFF proxy, but we also keep the viewer gate on the screen itself.
  if (adminSlug !== null) {
    const consoleState: ScreenState = roleAtLeast(role, "viewer")
      ? { kind: "ready" }
      : {
          kind: "restricted",
          reason:
            role === "unknown"
              ? "Your role has not been resolved yet, so this console is hidden until it is. The server enforces access regardless."
              : "This service console needs at least the viewer role.",
        };
    return (
      <ScreenScaffold
        title={title}
        subtitle={`Manage and monitor ${serviceLabel(service)} from the control plane.`}
        state={consoleState}
      >
        <ServiceAdminConsole slug={adminSlug} />
      </ScreenScaffold>
    );
  }

  const state = resolveState(valid, role, query);

  return (
    <ScreenScaffold
      title={title}
      subtitle="Golden signals, per-route table, instances and restarts, version and profile, heartbeat detail."
      state={state}
    >
      {valid && query.data !== undefined && (
        <ServiceBody service={service} data={query.data} live={live} window={window} />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the param validity, role and query status. */
function resolveState(
  valid: boolean,
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useService>["query"],
): ScreenState {
  // An unknown route param is not a service: an honest empty state, never a crash or a request.
  if (!valid) {
    return {
      kind: "empty",
      cause:
        "There is no such service. The address must name one of the KYA services (for example idp, ais, vci, gateway-mcp or resource).",
    };
  }

  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so this service view is hidden until it is. The server enforces access regardless."
          : "This service view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    const d = query.data;
    const empty =
      d.instances_total === 0 && d.routes.length === 0 && d.instances.length === 0;
    if (empty) {
      return {
        kind: "empty",
        cause:
          "This service has not reported any instances, routes or signals in this window. Widen the time range, or enroll a source for it.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (query.error.isWaking) return { kind: "loading" };
    if (query.error.kind === "not_found") {
      return {
        kind: "empty",
        cause: "The control plane does not know this service yet. It appears once a source reports for it.",
      };
    }
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useService>["query"]["error"]): string {
  if (error === null) return "This service could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see this service. The server enforces access.";
    case "network":
      return "The service request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function ServiceBody(props: {
  service: Service;
  data: ServiceDetail;
  live: boolean;
  window: ReturnType<typeof useService>["window"];
}): ReactNode {
  const { data, service } = props;
  const generatedAt = data.meta.generated_at;

  return (
    <div className="svc" data-live={props.live}>
      <SummaryBar service={service} data={data} />

      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="svc__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(props.window.fromMs)}>
          {formatTimeOfDay(props.window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(props.window.toMs)}>
          {formatTimeOfDay(props.window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
      </p>
      </div>

      <GoldenSignalsPanel service={service} signals={data.golden_signals} live={props.live} />
      <RouteTable service={service} routes={data.routes} />
      <InstancesTable instances={data.instances} />
    </div>
  );
}

/**
 * The header summary: the service's overall status (icon+text, never green when silent/unknown),
 * instance counts, versions and profiles, worst Protected_Core state, last heartbeat, a stale flag,
 * and the admin deep-link element with its tri-state. The admin link is placed here, next to the
 * identity of the service, where an operator expects a jump-out control.
 */
function SummaryBar(props: { service: Service; data: ServiceDetail }): ReactNode {
  const { data, service } = props;
  const silentNote =
    data.instances_silent > 0
      ? `${data.instances_silent} of ${data.instances_total} silent`
      : `${data.instances_total} instance${data.instances_total === 1 ? "" : "s"}`;

  return (
    <section className="svc-summary" aria-label="Service summary">
      <div className="svc-summary__status">
        <StatusBadge
          level={statusLevel(data.status)}
          label={statusText(data.status)}
          title={`${serviceLabel(service)}: ${statusText(data.status).toLowerCase()}`}
        />
        {data.stale && (
          <span className="svc-summary__flag" title="Data for this service is stale (a source is silent).">
            stale
          </span>
        )}
      </div>

      <dl className="svc-summary__facts">
        <div className="svc-summary__fact">
          <dt>Instances</dt>
          <dd>{silentNote}</dd>
        </div>
        <div className="svc-summary__fact">
          <dt>Versions</dt>
          <dd>{data.versions.length > 0 ? data.versions.join(", ") : <span className="svc-muted">n/a</span>}</dd>
        </div>
        <div className="svc-summary__fact">
          <dt>Profiles</dt>
          <dd>{data.profiles.length > 0 ? data.profiles.join(", ") : <span className="svc-muted">n/a</span>}</dd>
        </div>
        <div className="svc-summary__fact">
          <dt>Last heartbeat</dt>
          <dd>
            {data.last_heartbeat_at === null ? (
              <span className="svc-muted">n/a</span>
            ) : (
              <time
                dateTime={isoAttr(data.last_heartbeat_at)}
                title={isoAttr(data.last_heartbeat_at)}
              >
                {formatDateTime(data.last_heartbeat_at)}
              </time>
            )}
          </dd>
        </div>
      </dl>

      <div className="svc-summary__admin">
        <AdminDeepLink link={data.admin_link} serviceLabel={serviceLabel(service)} />
      </div>
    </section>
  );
}
